import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, RefreshCw, Activity, Bell, Mail, Sparkles, Database, HardDrive, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { toast } from "sonner";

const StatusDot = ({ ok }) => (
  <span
    className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-[color:var(--moss)]" : "bg-[#DC2626]"}`}
    aria-hidden
  />
);

const RowKV = ({ label, value, mono = false }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-[color:var(--bg-elevated)] last:border-0">
    <span className="text-xs text-[color:var(--text-secondary)]">{label}</span>
    <span className={`text-xs ${mono ? "font-mono" : ""} text-right`}>{value ?? "—"}</span>
  </div>
);

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });
  } catch (_) {
    return iso;
  }
};

const fmtDuration = (ms) => {
  if (ms == null) return "—";
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
};

export default function HealthPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/health/dashboard");
      setData(r.data);
    } catch (e) {
      toast.error("Kunde inte hämta health-data");
    } finally {
      setLoading(false);
    }
  };

  // Version: jämför preview-backend vs Railway-prod-backend.
  const [version, setVersion] = useState({ preview: null, prod: null, prodErr: null });
  const loadVersion = async () => {
    setVersion({ preview: null, prod: null, prodErr: null });
    try {
      const v = await api.get("/version");
      setVersion((s) => ({ ...s, preview: v.data }));
    } catch {}
    // Prod = Railway. Backend-url hämtas från ENV REACT_APP_PROD_BACKEND_URL om satt.
    const prodUrl = process.env.REACT_APP_PROD_BACKEND_URL;
    if (!prodUrl) {
      setVersion((s) => ({ ...s, prodErr: "REACT_APP_PROD_BACKEND_URL ej satt" }));
      return;
    }
    try {
      const res = await fetch(`${prodUrl}/api/version`, { credentials: "omit" });
      if (res.ok) {
        const json = await res.json();
        setVersion((s) => ({ ...s, prod: json }));
      } else {
        setVersion((s) => ({ ...s, prodErr: `HTTP ${res.status}` }));
      }
    } catch (e) {
      setVersion((s) => ({ ...s, prodErr: e.message || "nätfel" }));
    }
  };

  useEffect(() => {
    load();
    loadVersion();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const trigger = async (key, fn, successMsg) => {
    setBusyKey(key);
    try {
      const res = await fn();
      toast.success(successMsg(res?.data));
      load();
    } catch (e) {
      toast.error("Fel: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusyKey(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="px-5 pt-3" data-testid="health-page">
        <p className="text-sm text-[color:var(--text-tertiary)]">Laddar status…</p>
      </div>
    );
  }
  if (!data) return null;

  const playwright = data.integrations?.playwright || {};
  const commhubOk = !!data.integrations?.commhub?.configured;
  const llmOk = !!data.integrations?.emergent_llm?.configured;
  const playwrightOk = !!playwright.ok && (playwright.browsers || []).some((b) => b.includes("1217") || !b.match(/-\d+$/));
  const briefingJob = data.briefing?.latest_job;
  const dup = data.db_counts?.events_duplicates || 0;

  return (
    <div className="px-5 pt-3 pb-12" data-testid="health-page">
      <button
        onClick={() => nav("/installningar")}
        className="flex items-center gap-1 text-sm text-[color:var(--text-secondary)] mb-4"
        data-testid="health-back"
      >
        <ArrowLeft size={16} /> Inställningar
      </button>

      <div className="mb-6 flex items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow flex items-center gap-1.5">
            <Activity size={12} /> Backend health
          </p>
          <h1 className="font-display text-2xl font-medium mt-0.5">Hälsa & test</h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-[11px] text-[color:var(--text-secondary)]">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              data-testid="health-auto-refresh"
            />
            Auto
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            className="rounded-full h-8 text-xs"
            data-testid="health-refresh"
          >
            <RefreshCw size={12} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
            Uppdatera
          </Button>
        </div>
      </div>

      {/* Version-jämförelse: preview vs prod */}
      <div className="card-soft p-3 mb-6" data-testid="health-tile-version">
        <div className="flex items-center justify-between mb-2">
          <p className="label-eyebrow">Version & deploy</p>
          <button
            onClick={loadVersion}
            className="text-[11px] text-[color:var(--text-secondary)] hover:text-[color:var(--moss)]"
            data-testid="version-refresh"
          >
            <RefreshCw size={11} className="inline mr-1" /> Hämta om
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1">Preview</p>
            {version.preview ? (
              <div className="space-y-0.5">
                <p className="font-mono flex items-center gap-1.5">
                  {version.preview.git_short}
                  {version.preview.dirty && (
                    <span className="text-[10px] px-1 py-0.5 rounded bg-[#FEF3C7] text-[#92400E] font-semibold" title={`${version.preview.dirty_files} okommitterade filer`}>
                      +{version.preview.dirty_files} okommitterade
                    </span>
                  )}
                </p>
                <p className="text-[color:var(--text-tertiary)]">{version.preview.build_number}</p>
                <p className="text-[10px] text-[color:var(--text-tertiary)]">{version.preview.environment} · upp {fmtDuration(Date.now() - new Date(version.preview.started_at).getTime())}</p>
              </div>
            ) : (
              <span className="text-[color:var(--text-tertiary)]">laddar…</span>
            )}
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1">Prod (Railway)</p>
            {version.prod ? (
              <div className="space-y-0.5">
                <p className="font-mono flex items-center gap-1.5">
                  {version.prod.git_short}
                  {version.preview && version.prod.git_commit === version.preview.git_commit && !version.preview.dirty ? (
                    <span className="text-[10px] px-1 py-0.5 rounded bg-[#E6F1E2] text-[color:var(--moss)] font-semibold">i synk</span>
                  ) : version.preview && version.prod.git_commit === version.preview.git_commit && version.preview.dirty ? (
                    <span className="text-[10px] px-1 py-0.5 rounded bg-[#FEF3C7] text-[#92400E] font-semibold" title="Samma commit men preview har okommitterade ändringar">behöver push</span>
                  ) : version.preview ? (
                    <span className="text-[10px] px-1 py-0.5 rounded bg-[#FEF2F2] text-[#DC2626] font-semibold">olika</span>
                  ) : null}
                </p>
                <p className="text-[color:var(--text-tertiary)]">{version.prod.build_number}</p>
                <p className="text-[10px] text-[color:var(--text-tertiary)]">{version.prod.environment} · upp {fmtDuration(Date.now() - new Date(version.prod.started_at).getTime())}</p>
              </div>
            ) : version.prodErr ? (
              <span className="text-[#DC2626]">{version.prodErr}</span>
            ) : (
              <span className="text-[color:var(--text-tertiary)]">laddar…</span>
            )}
          </div>
        </div>
      </div>

      {/* Översikt */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="card-soft p-3" data-testid="health-tile-integrations">
          <p className="label-eyebrow mb-1.5">Integrationer</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs">
              <StatusDot ok={commhubOk} /> Commhub {commhubOk ? "OK" : "saknas"}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <StatusDot ok={llmOk} /> Emergent LLM {llmOk ? "OK" : "saknas"}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <StatusDot ok={playwrightOk} /> Playwright {playwrightOk ? "OK" : "fel"}
            </div>
          </div>
        </div>
        <div className="card-soft p-3" data-testid="health-tile-db">
          <p className="label-eyebrow mb-1.5">Databas</p>
          <RowKV label="Events" value={data.db_counts?.events_cache} mono />
          <RowKV label="Användare" value={data.db_counts?.users} mono />
          <RowKV label="Öppna todos" value={data.db_counts?.todos_open} mono />
          <RowKV
            label="Dubbletter"
            value={
              <span className={dup > 0 ? "text-[#DC2626] font-semibold" : "text-[color:var(--moss)]"}>
                {dup === 0 ? "0 ✓" : dup}
              </span>
            }
          />
        </div>
        <div className="card-soft p-3" data-testid="health-tile-push">
          <p className="label-eyebrow mb-1.5">Push & quiet hours</p>
          <RowKV label="Enheter (på)" value={data.push?.device_count_enabled} mono />
          <RowKV
            label="Tyst nu?"
            value={data.push?.quiet_hours?.enabled === false ? "Av" : `${data.push?.quiet_hours?.start || "21:00"}–${data.push?.quiet_hours?.end || "07:00"}`}
          />
          <RowKV label="I kö" value={data.push?.pending_notifications} mono />
        </div>
        <div className="card-soft p-3" data-testid="health-tile-disk">
          <p className="label-eyebrow mb-1.5 flex items-center gap-1">
            <HardDrive size={11} /> Disk
          </p>
          {data.disk?.error ? (
            <p className="text-xs text-[color:var(--text-tertiary)]">Otillgänglig</p>
          ) : (
            <>
              <RowKV label="Använt" value={`${data.disk?.used_gb} GB (${data.disk?.used_pct}%)`} mono />
              <RowKV label="Ledigt" value={`${data.disk?.free_gb} GB`} mono />
              <RowKV label="Totalt" value={`${data.disk?.total_gb} GB`} mono />
            </>
          )}
        </div>
        <div className="card-soft p-3 col-span-2" data-testid="health-tile-railway">
          <p className="label-eyebrow mb-1.5 flex items-center gap-1">
            🚂 Railway (senaste 24h)
          </p>
          {(() => {
            const rw = data.integrations?.railway || {};
            if (!rw.ok) {
              return <p className="text-xs text-[color:var(--text-tertiary)]">Otillgänglig: {rw.error || "okänt fel"}</p>;
            }
            const has5xx = (rw.errors_5xx || 0) > 0;
            return (
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-[color:var(--text-tertiary)] font-semibold">Tjänster</p>
                  <p className="text-xs font-medium">{rw.services_healthy ?? "–"}/{rw.services_total ?? "–"} OK</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-[color:var(--text-tertiary)] font-semibold">5xx-fel</p>
                  <p className={`text-xs font-medium ${has5xx ? "text-[#DC2626]" : "text-[color:var(--moss)]"}`}>
                    {rw.errors_5xx ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-[color:var(--text-tertiary)] font-semibold">4xx-fel</p>
                  <p className="text-xs font-medium">{rw.errors_4xx ?? 0}</p>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Feeds */}
      <section className="mb-6" data-testid="health-feeds">
        <h2 className="font-display text-base font-medium mb-2 flex items-center gap-1.5">
          <Database size={14} /> iCal-feeds
        </h2>
        <div className="card-soft divide-y divide-[color:var(--bg-elevated)]">
          {(data.feeds || []).map((f) => (
            <div key={f.id} className="p-3 flex items-center gap-2.5" data-testid={`health-feed-${f.id}`}>
              {f.last_error ? (
                <AlertCircle size={14} className="text-[#DC2626] flex-shrink-0" />
              ) : (
                <CheckCircle2 size={14} className="text-[color:var(--moss)] flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {f.name} <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)]">· {f.owner || "—"}</span>
                </p>
                <p className="text-[11px] text-[color:var(--text-tertiary)] truncate">
                  {f.event_count ?? 0} händelser · senast {fmtDateTime(f.last_synced)}
                  {f.last_error ? ` · ${f.last_error.slice(0, 60)}` : ""}
                </p>
              </div>
            </div>
          ))}
          {(data.feeds || []).length === 0 && (
            <p className="p-4 text-xs text-[color:var(--text-tertiary)] text-center">Inga feeds.</p>
          )}
        </div>
      </section>

      {/* Scheduler */}
      <section className="mb-6" data-testid="health-scheduler">
        <h2 className="font-display text-base font-medium mb-2 flex items-center gap-1.5">
          <Clock size={14} /> Scheduler-jobb
        </h2>
        <div className="card-soft p-3 space-y-2">
          {(Array.isArray(data.scheduler) ? data.scheduler : []).map((job) => (
            <div key={job.id} className="text-xs" data-testid={`scheduler-${job.id}`}>
              <p className="font-medium">{job.label}</p>
              <p className="text-[11px] text-[color:var(--text-tertiary)]">
                {job.last_run ? (
                  <>
                    senast {fmtDateTime(job.last_run)} ({fmtDuration(job.duration_ms)})
                    {job.errors > 0 && (
                      <span className="ml-1 text-[#DC2626]">· {job.errors} fel</span>
                    )}
                  </>
                ) : (
                  "har inte körts ännu"
                )}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Briefing-job */}
      {briefingJob && (
        <section className="mb-6" data-testid="health-briefing-job">
          <h2 className="font-display text-base font-medium mb-2 flex items-center gap-1.5">
            <Sparkles size={14} className="text-[color:var(--jarvis)]" /> Senaste manuella briefing
          </h2>
          <div className="card-soft p-3 space-y-1">
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-semibold ${
                  briefingJob.status === "running"
                    ? "bg-[#FFF3CD] text-[#664D03]"
                    : briefingJob.status === "done"
                    ? "bg-[#F0F6E6] text-[color:var(--moss)]"
                    : "bg-[#FBE6E6] text-[#DC2626]"
                }`}
              >
                {briefingJob.status}
              </span>
              <span className="text-[11px] text-[color:var(--text-tertiary)]">
                {briefingJob.started_by} · {fmtDateTime(briefingJob.started_at)}
              </span>
            </div>
            <RowKV label="Användare" value={`${briefingJob.users_processed}/${briefingJob.users_total}`} />
            <RowKV label="Push skickad" value={briefingJob.push_sent} mono />
            <RowKV label="Mail skickade" value={briefingJob.emails_sent} mono />
            <RowKV label="Tid" value={fmtDuration(briefingJob.duration_ms)} />
            {briefingJob.error && (
              <p className="text-xs text-[#DC2626] mt-1">{briefingJob.error}</p>
            )}
          </div>
        </section>
      )}

      {/* Test-triggers */}
      <section className="mb-6" data-testid="health-tests">
        <h2 className="font-display text-base font-medium mb-2">Test-triggers</h2>
        <p className="text-xs text-[color:var(--text-secondary)] mb-3">
          Snabba sätt att verifiera att rätt komponent fungerar — utan att vänta på scheduler-cyklerna.
        </p>
        <div className="grid grid-cols-1 gap-2">
          {/* Master: Sync all — kör iCal + Värmdö Classroom + Värmdö Gmail + Vklass deep-scrape parallellt */}
          <Button
            variant="default"
            className="justify-start rounded-xl h-auto py-3 bg-[color:var(--jarvis)] hover:opacity-90 text-white"
            disabled={busyKey === "sync-all"}
            onClick={() =>
              trigger(
                "sync-all",
                () => api.post("/health/sync-all"),
                (r) =>
                  r?.ok
                    ? `${r?.summary || "Allt synkat"} (${r?.duration_sec}s)`
                    : `${r?.summary || "Vissa fel"} — kolla detalj-output`,
                120000,
              )
            }
            data-testid="health-sync-all"
          >
            <RefreshCw size={14} className={`mr-2 ${busyKey === "sync-all" ? "animate-spin" : ""}`} />
            <span className="flex-1 text-left">
              <span className="block text-sm font-semibold">Sync ALL nu</span>
              <span className="block text-[11px] opacity-80">
                iCal + Värmdö Classroom + Värmdö Gmail + Vklass deep-scrape (parallellt, ~30-120s)
              </span>
            </span>
            {busyKey === "sync-all" && <RefreshCw size={12} className="animate-spin" />}
          </Button>

          <Button
            variant="outline"
            className="justify-start rounded-xl h-auto py-3"
            disabled={busyKey === "push"}
            onClick={() =>
              trigger(
                "push",
                () => api.post("/health/test/push", {}),
                (r) => `Push-test skickad (${r?.push_sent || 0} levererad)`,
              )
            }
            data-testid="health-test-push"
          >
            <Bell size={14} className="mr-2 text-[color:var(--moss)]" />
            <span className="flex-1 text-left">
              <span className="block text-sm font-medium">Test-push till mig</span>
              <span className="block text-[11px] text-[color:var(--text-tertiary)]">Skickar genast (kringgår quiet hours)</span>
            </span>
            {busyKey === "push" && <RefreshCw size={12} className="animate-spin" />}
          </Button>

          <Button
            variant="outline"
            className="justify-start rounded-xl h-auto py-3"
            disabled={busyKey === "email"}
            onClick={() =>
              trigger(
                "email",
                () => api.post("/health/test/email", {}),
                (r) => (r?.ok ? `Test-mail skickat till ${r?.to}` : "Mail misslyckades"),
              )
            }
            data-testid="health-test-email"
          >
            <Mail size={14} className="mr-2 text-[color:var(--moss)]" />
            <span className="flex-1 text-left">
              <span className="block text-sm font-medium">Test-mail till mig</span>
              <span className="block text-[11px] text-[color:var(--text-tertiary)]">Via Commhub till din primära mail</span>
            </span>
            {busyKey === "email" && <RefreshCw size={12} className="animate-spin" />}
          </Button>

          <Button
            variant="outline"
            className="justify-start rounded-xl h-auto py-3"
            disabled={busyKey === "feeds"}
            onClick={() =>
              trigger(
                "feeds",
                () => api.post("/health/test/feed-sync"),
                (r) => `${r?.synced || 0} feeds synkade`,
              )
            }
            data-testid="health-test-feeds"
          >
            <RefreshCw size={14} className="mr-2 text-[color:var(--moss)]" />
            <span className="flex-1 text-left">
              <span className="block text-sm font-medium">Synka alla iCal-feeds</span>
              <span className="block text-[11px] text-[color:var(--text-tertiary)]">Hämtar alla URL:er parallellt</span>
            </span>
            {busyKey === "feeds" && <RefreshCw size={12} className="animate-spin" />}
          </Button>

          <Button
            variant="outline"
            className="justify-start rounded-xl h-auto py-3"
            disabled={busyKey === "jarvis"}
            onClick={() =>
              trigger(
                "jarvis",
                () => api.post("/health/test/jarvis", {}),
                (r) => (r?.ok ? `Edit: "${(r?.reply || "").slice(0, 60)}"` : `Edit fel: ${r?.error}`),
              )
            }
            data-testid="health-test-jarvis"
          >
            <Sparkles size={14} className="mr-2 text-[color:var(--jarvis)]" />
            <span className="flex-1 text-left">
              <span className="block text-sm font-medium">Pinga Edit (Claude)</span>
              <span className="block text-[11px] text-[color:var(--text-tertiary)]">Verifierar att LLM-nyckeln fungerar</span>
            </span>
            {busyKey === "jarvis" && <RefreshCw size={12} className="animate-spin" />}
          </Button>
        </div>
      </section>

      <p className="text-[10px] text-[color:var(--text-tertiary)] text-center mt-4">
        Snapshot kl {fmtDateTime(data.now)}
      </p>
    </div>
  );
}
